// Common stuff for cloud-like icon design - currently collapsed path or nodes (cloud) and transit

import {
  type NonZeroVector,
  type Point,
  point,
  segment,
  vector,
} from '../../elements';
import { findCircleVectorIntersections } from '../../elements/circle';
import type { NonZeroSegment } from '../../elements/segment';
import { nonEmptyArray } from '../../types/NonEmptyArray';
import { findByMax } from '../../utils/numbers';
import type { RGBColorString } from '../../utils/rgb';
import type { NodeStyle } from '../NodeStyle';
import { nodeStyleBackgroundColors, nodeStyleOutlineColors } from './style';

const CLOUDY_BACKGROUND_COLOR = '#bac7db';
const CLOUDY_STROKE_COLOR = '#4b4c4e';

export const getCloudyColors = (
  nodeStyle: NodeStyle,
): { fill: RGBColorString | 'none'; stroke: RGBColorString } => ({
  fill:
    nodeStyle.background === 'blue' || nodeStyle.background === undefined
      ? CLOUDY_BACKGROUND_COLOR
      : (nodeStyleBackgroundColors[nodeStyle.background] ?? 'none'),

  stroke:
    nodeStyle.outline === undefined
      ? CLOUDY_STROKE_COLOR
      : nodeStyleOutlineColors[nodeStyle.outline],
});

export const makeGetCloudyClippingPoint = (elements: {
  circles?: [center: Point, radius: number][];
  segments?: NonZeroSegment[];
}): ((direction: NonZeroVector) => Point) => {
  const findIntersectionList = <T>(
    list: T[],
    findElementIntersections: (el: T) => Point[],
  ): Point[] =>
    list.reduce<Point[]>((acc, el) => {
      acc.push(...findElementIntersections(el));
      return acc;
    }, []);

  return (direction: NonZeroVector): Point => {
    const intersections = [
      ...findIntersectionList(elements.circles ?? [], ([cloudCircle, radius]) =>
        findCircleVectorIntersections(cloudCircle, radius, direction),
      ),
      ...findIntersectionList(elements.segments ?? [], (s) =>
        segment.findVectorIntersection(s, direction),
      ),
    ];
    return nonEmptyArray.isNonEmpty(intersections)
      ? findByMax(intersections, vector.getMagnitude)
      : point.zero;
  };
};
